import Phaser from "phaser";
import { GAME_DURATION } from "../constants";
import type { HudPayload } from "../types";
import { HUD_LAYOUT, HUD_PALETTE, type HudMeterIcon } from "../ui/hudLayoutConfig";

export class HudScene extends Phaser.Scene {
  private timeLabel!: Phaser.GameObjects.Text;
  private timeValue!: Phaser.GameObjects.Text;
  private scoreValue!: Phaser.GameObjects.Text;
  private comboText!: Phaser.GameObjects.Text;
  private energiaBar!: Phaser.GameObjects.Rectangle;
  private energiaLabel!: Phaser.GameObjects.Text;
  private instintoBar!: Phaser.GameObjects.Rectangle;
  private auraBar!: Phaser.GameObjects.Rectangle;
  private storyHint!: Phaser.GameObjects.Text;
  private debugText!: Phaser.GameObjects.Text;
  private paused = false;

  constructor() {
    super("HudScene");
  }

  create() {
    this.drawStrip();
    this.createTimeModule();
    this.createScoreModule();
    this.createMeters();
    this.createButtons();
    this.createComboAndHint();
    this.createDebug();

    this.game.events.on("hud:update", (payload: HudPayload) => this.updateHud(payload));
    this.game.events.on("story:hint", (payload: string | { text: string; durationMs?: number }) => this.showStoryHint(payload));
    this.game.events.on("hud:pause", (value: boolean) => {
      this.paused = value;
      this.pauseButton.setText(value ? ">" : "II");
      // Hold/resume only the cosmetic story-hint tween so the pause is 100% still.
      // Targeted by object (not pauseAll) so the pause button stays responsive.
      this.tweens.getTweensOf(this.storyHint).forEach((tween) => (value ? tween.pause() : tween.resume()));
    });
  }

  // ── Static frame: panel + vertical dividers ────────────────────────────────
  private drawStrip() {
    const { strip, dividers, dividerTop, dividerBottom } = HUD_LAYOUT;
    const panel = this.add.graphics();
    panel.fillStyle(HUD_PALETTE.panelFill, HUD_PALETTE.panelFillAlpha);
    panel.fillRoundedRect(strip.x, strip.y, strip.width, strip.height, strip.radius);
    panel.lineStyle(1, HUD_PALETTE.panelStroke, HUD_PALETTE.panelStrokeAlpha);
    panel.strokeRoundedRect(strip.x, strip.y, strip.width, strip.height, strip.radius);
    panel.lineStyle(1, HUD_PALETTE.divider, HUD_PALETTE.dividerAlpha);
    dividers.forEach((x) => {
      panel.lineBetween(x, dividerTop, x, dividerBottom);
    });
  }

  // ── TIEMPO module (countdown number) ───────────────────────────────────────
  private createTimeModule() {
    const { label, value } = HUD_LAYOUT.time;
    this.timeLabel = this.add.text(label.x, label.y, "TIEMPO", this.labelStyle(HUD_PALETTE.energia.label));
    this.timeValue = this.add.text(value.x, value.y, "120.00", {
      fontFamily: "Inter, Arial, sans-serif",
      fontSize: value.fontSize,
      fontStyle: "900",
      color: HUD_PALETTE.timeText
    });
  }

  // ── SCORE module ───────────────────────────────────────────────────────────
  private createScoreModule() {
    const { label, value } = HUD_LAYOUT.score;
    this.add.text(label.x, label.y, "SCORE", this.labelStyle(HUD_PALETTE.energia.label));
    this.scoreValue = this.add.text(value.x, value.y, "0", {
      fontFamily: "Inter, Arial, sans-serif",
      fontSize: value.fontSize,
      fontStyle: "900",
      color: HUD_PALETTE.scoreText
    });
  }

  // ── Three separated meters: ENERGIA / INSTINTO / AURA ──────────────────────
  private createMeters() {
    const m = HUD_LAYOUT.meters;
    const icons = this.add.graphics();

    const energia = this.buildMeter(icons, m.rows.energia.y, m.rows.energia.icon, m.rows.energia.label, HUD_PALETTE.energia.bar, HUD_PALETTE.energia.label);
    this.energiaBar = energia.bar;
    this.energiaLabel = energia.label;
    this.instintoBar = this.buildMeter(icons, m.rows.instinto.y, m.rows.instinto.icon, m.rows.instinto.label, HUD_PALETTE.instinto.bar, HUD_PALETTE.instinto.label).bar;
    this.auraBar = this.buildMeter(icons, m.rows.aura.y, m.rows.aura.icon, m.rows.aura.label, HUD_PALETTE.aura.bar, HUD_PALETTE.aura.label).bar;
  }

  private buildMeter(icons: Phaser.GameObjects.Graphics, y: number, icon: HudMeterIcon, label: string, barColor: number, labelColor: string) {
    const m = HUD_LAYOUT.meters;
    this.drawMeterIcon(icons, m.iconX, y, icon, barColor);
    const labelText = this.add.text(m.labelX, y, label, this.labelStyle(labelColor, m.labelSize)).setOrigin(0, 0.5);
    this.add.rectangle(m.barX, y, m.barWidth, m.trackHeight, HUD_PALETTE.track, HUD_PALETTE.trackAlpha).setOrigin(0, 0.5);
    const bar = this.add.rectangle(m.barX, y, 0, m.barHeight, barColor, m.fillAlpha).setOrigin(0, 0.5);
    return { bar, label: labelText };
  }

  private drawMeterIcon(g: Phaser.GameObjects.Graphics, x: number, y: number, icon: HudMeterIcon, color: number) {
    const s = HUD_LAYOUT.meters.iconSize;
    g.fillStyle(color, 1);
    if (icon === "diamond") {
      g.beginPath();
      g.moveTo(x, y - s);
      g.lineTo(x + s, y);
      g.lineTo(x, y + s);
      g.lineTo(x - s, y);
      g.closePath();
      g.fillPath();
    } else if (icon === "triangle") {
      g.beginPath();
      g.moveTo(x, y - s);
      g.lineTo(x + s, y + s * 0.8);
      g.lineTo(x - s, y + s * 0.8);
      g.closePath();
      g.fillPath();
    } else {
      g.fillCircle(x, y, s * 0.9);
    }
  }

  // ── Pause / mute buttons (identical events — pause fix depends on this) ─────
  private pauseButton!: Phaser.GameObjects.Text;

  private createButtons() {
    const { pause, mute } = HUD_LAYOUT.buttons;
    this.pauseButton = this.add
      .text(pause.x, pause.y, "II", this.buttonStyle(HUD_PALETTE.timeText))
      .setInteractive({ useHandCursor: true });
    this.pauseButton.on("pointerdown", () => this.game.events.emit("game:pause-toggle"));

    const music = this.add
      .text(mute.x, mute.y, "M", this.buttonStyle(HUD_PALETTE.energia.label))
      .setInteractive({ useHandCursor: true });
    music.on("pointerdown", () => this.game.events.emit("audio:mute-toggle"));
  }

  private createComboAndHint() {
    this.comboText = this.add
      .text(HUD_LAYOUT.combo.x, HUD_LAYOUT.combo.y, "", {
        fontFamily: "Inter, Arial, sans-serif",
        fontSize: "11px",
        fontStyle: "900",
        color: HUD_PALETTE.scoreText,
        stroke: "#050708",
        strokeThickness: 3
      })
      .setOrigin(0.5, 0)
      .setAlpha(0);
    this.storyHint = this.add
      .text(HUD_LAYOUT.storyHint.x, HUD_LAYOUT.storyHint.yRest, "", {
        fontFamily: "Inter, Arial, sans-serif",
        fontSize: "12px",
        fontStyle: "800",
        color: HUD_PALETTE.scoreText
      })
      .setOrigin(0.5, 0)
      .setAlpha(0);
  }

  private createDebug() {
    this.debugText = this.add
      .text(HUD_LAYOUT.debug.x, HUD_LAYOUT.debug.y, "", {
        fontFamily: "Consolas, monospace",
        fontSize: "13px",
        color: HUD_PALETTE.scoreText,
        backgroundColor: "rgba(5,7,8,0.6)",
        padding: { x: 8, y: 6 }
      })
      .setVisible(false);
  }

  private showStoryHint(payload: string | { text: string; durationMs?: number }) {
    const text = typeof payload === "string" ? payload : payload.text;
    const durationMs = typeof payload === "string" ? 1100 : payload.durationMs ?? 1000;
    this.storyHint.setText(text);
    this.tweens.killTweensOf(this.storyHint);
    this.storyHint.setAlpha(0).setY(HUD_LAYOUT.storyHint.yRest);
    this.tweens.add({
      targets: this.storyHint,
      alpha: 0.72,
      y: HUD_LAYOUT.storyHint.yLift,
      duration: 260,
      ease: "Sine.easeOut",
      yoyo: true,
      hold: durationMs
    });
  }

  private updateHud(payload: HudPayload) {
    const active = payload.phase === "playing" || payload.phase === "paused";
    const remaining = Math.max(0, GAME_DURATION - payload.elapsed);

    this.timeValue.setText(remaining.toFixed(2)).setAlpha(active ? 1 : 0.42);
    this.timeLabel.setText(this.paused ? "PAUSA" : "TIEMPO").setColor(this.paused ? HUD_PALETTE.instinto.label : HUD_PALETTE.energia.label);

    this.scoreValue.setText(Math.floor(payload.score).toLocaleString("en-US")).setAlpha(active ? 1 : 0.5);

    const comboVisible = payload.combo >= 2 && active;
    this.comboText.setText(comboVisible ? `CADENA ${payload.combo}  x${payload.multiplier.toFixed(2).replace(/\.00$/, "")}` : "");
    this.comboText.setAlpha(comboVisible ? 0.92 : 0);

    // ENERGIA ← musicEnergy (with low-energy warning state).
    const energiaLow = payload.musicEnergy < HUD_PALETTE.energia.lowThreshold;
    this.energiaBar.width = HUD_LAYOUT.meters.barWidth * Phaser.Math.Clamp(payload.musicEnergy, 0, 1);
    this.energiaBar.setFillStyle(energiaLow ? HUD_PALETTE.energia.barLow : HUD_PALETTE.energia.bar, payload.musicActive ? 0.9 : 0.34);
    this.energiaLabel.setColor(energiaLow ? HUD_PALETTE.energia.labelLow : HUD_PALETTE.energia.label);

    // INSTINTO ← danger, AURA ← champion charge (energy / 4). Fixed brand colors.
    this.instintoBar.width = HUD_LAYOUT.meters.barWidth * Phaser.Math.Clamp(payload.danger, 0, 1);
    this.auraBar.width = HUD_LAYOUT.meters.barWidth * Phaser.Math.Clamp(payload.energy / 4, 0, 1);

    this.debugText.setVisible(payload.debug);
    if (payload.debug) {
      this.debugText.setText([
        `fps ${payload.fps.toFixed(1)}`,
        `time ${payload.elapsed.toFixed(2)}`,
        `danger ${payload.danger.toFixed(2)}`,
        `song ${(payload.musicEnergy * 100).toFixed(0)}% ${payload.musicActive ? "on" : "low"}`,
        `wolf ${payload.wolfState} ${payload.wolfStamina.toFixed(0)}%`,
        `terrain ${payload.terrain}`,
        `phase ${payload.phase}`
      ]);
    }
  }

  private labelStyle(color: string, fontSize = "9px"): Phaser.Types.GameObjects.Text.TextStyle {
    return { fontFamily: "Inter, Arial, sans-serif", fontSize, fontStyle: "900", color };
  }

  private buttonStyle(color: string): Phaser.Types.GameObjects.Text.TextStyle {
    return {
      fontFamily: "Inter, Arial, sans-serif",
      fontSize: HUD_LAYOUT.buttons.fontSize,
      fontStyle: "900",
      color,
      backgroundColor: "rgba(5,7,8,0.36)",
      padding: HUD_LAYOUT.buttons.padding
    };
  }
}
